import type { QuestionResponseSchema } from "@/hera-api/client/types.gen";
import React from "react";

// Reusable component for Information questions
function InformationField({
  content,
  question,
}: { content: string; question: string }) {
  return (
    <div className="space-y-2 p-4 border rounded-md bg-yellow-50">
      <h3 className="font-medium text-sm text-yellow-800">{question}</h3>
      <div className="space-y-1">
        <div
          className="text-sm text-yellow-800 [&>ul]:list-disc [&>ul]:ml-4 [&>ol]:list-decimal [&>ol]:ml-4 [&>p]:mb-4 [&>p:last-child]:mb-0 [&>h1]:text-sm [&>h1]:font-bold [&>h1]:mb-4 [&>h2]:text-sm [&>h2]:font-bold [&>h2]:mb-3 [&>blockquote]:border-l-4 [&>blockquote]:border-yellow-800/20 [&>blockquote]:pl-4 [&>blockquote]:italic"
          // biome-ignore lint/security/noDangerouslySetInnerHtml: Content comes from trusted rich text editor
          dangerouslySetInnerHTML={{ __html: content }}
        />
      </div>
    </div>
  );
}

interface EarlyInformationalQuestionsProps {
  questionsData?: {
    questions: QuestionResponseSchema[];
  };
}

export function EarlyInformationalQuestions({
  questionsData,
}: EarlyInformationalQuestionsProps) {
  // Filter to registration-only, then informational questions marked to display up front
  const earlyInformationalQuestions = React.useMemo(() => {
    if (!questionsData?.questions) return [];

    return questionsData.questions
      .filter((q) => q.question_type === "registration")
      .filter(
        (q) =>
          q.answer_type === "information" &&
          q.information_text &&
          q.display_up_front === true,
      )
      .sort((a, b) => (a.display_order ?? 999) - (b.display_order ?? 999));
  }, [questionsData?.questions]);

  if (earlyInformationalQuestions.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      {earlyInformationalQuestions.map((question) => (
        <InformationField
          key={question.id}
          content={question.information_text ?? ""}
          question={question.question}
        />
      ))}
    </div>
  );
}
